import { Title } from '../titles/types/title';

export namespace Layout {
  export interface PageLink {
    type: Layout.SectionItemType | undefined;
    style?: Layout.PageViewStyleType;
  }

  export enum MenuLinkType {
    UNMANAGED = 'unmanaged',
    PAGE = 'page',
    LINK = 'link',
    MENU = 'menu',
  }

  export interface RootMenu {
    items: SectionItem[];
    label: string;
    slug: string;
  }

  export interface CollectionDetailsViewModel extends CollectionViewModel {
    items: SectionItem[];
  }

  export interface CollectionViewModel {
    name: string;
    slug: string;
    link: string;
    tags: string[];
  }

  export interface PageViewModelBase {
    label: string;
    slug: string;
    style: string;
  }

  export interface PageView {
    label: string;
    slug: string;
    style: PageViewStyleType;
    sections: SectionView[];
    theme: PageTheme;

    /**
     * * API response status if error
     */
    status?: number;
  }

  export interface PageTheme {
    name: string;
    backgroundColor: string;
    backgroundImage: string;
    icon: string;
    primaryColor: string;
    secondaryColor: string;
  }

  export interface SectionView {
    slug: string;
    label: string;
    link: string;
    style: SectionStyle;
  }

  export interface SectionStyle {
    type: `${SectionStyleType}`;
    pattern: `${PageSectionStylePattern}`;
  }

  export type Pattern = `${PageSectionStylePattern}`;

  export interface SectionItems {
    totalCount: number;
    data: SectionItem[];
    /** Not from API */
    link: string;
  }

  export namespace SectionViews {
    export interface Swimlane extends SectionView {
      style: SectionStyle;
    }
  }

  export type SectionItemType = 'title' | 'page' | 'link' | 'unmanaged' | 'menu' | 'image' | 'trailer';

  export interface BaseSectionItem {
    type: SectionItemType;
    id: string;
    slug: string;
    name: string;
    image: string;
    icon: string;
    pageStyle: PageViewStyleType;
    params: SectionItemParams | null;
    title?: Title | null;
    price?: number;
    isRoot?: boolean;
  }

  export interface SectionItemTitle extends BaseSectionItem {
    type: 'title';
    title: Title;
    params: SectionItemTitleParams | null;
  }

  export interface SectionItemPage extends BaseSectionItem {
    type: 'page';
    params: SectionItemPageParams | null;
    price: undefined;
  }

  export interface SectionItemLink extends BaseSectionItem {
    type: 'link';
    params: SectionItemLinkParams;
    price: undefined;
  }

  export interface SectionItemUnmanaged extends BaseSectionItem {
    type: 'unmanaged';
    params: SectionItemUnmanagedParams;
    price: undefined;
  }

  export interface SectionItemMenu extends BaseSectionItem {
    type: 'menu';
    params: SectionItemParams;
    price: undefined;
  }

  export interface SectionItemImage extends BaseSectionItem {
    type: 'image';
    params: null;
    price: undefined;
  }

  export interface SectionItemTrailer extends BaseSectionItem {
    type: 'trailer';
    params: null;
    price: undefined;
  }

  export type SectionItem =
    | SectionItemTitle
    | SectionItemPage
    | SectionItemLink
    | SectionItemUnmanaged
    | SectionItemMenu
    | SectionItemImage
    | SectionItemTrailer;

  export interface SectionItemTitleParams {
    TITLE_KEY: string;
    orderProvidersById?: string;
    isPromo?: 'true';
  }

  export interface SectionItemPageParams {
    behavior?: SectionItemBehavior;
    isPromo?: 'true';
  }

  export interface SectionItemLinkParams {
    url: string;
    providerIdentifier: string;
    linkTitle: string;
    behavior?: SectionItemBehavior;
    condition?: SectionItemCondition;
    isPromo?: 'true';
  }

  export interface SectionItemUnmanagedParams {
    action?: SectionItemAction;
    behavior?: SectionItemBehavior;
    isPromo?: 'true';
  }

  export type SectionItemParams =
    | SectionItemTitleParams
    | SectionItemPageParams
    | SectionItemLinkParams
    | SectionItemUnmanagedParams;

  export type SectionItemBehavior = 'see-all' | 'external-streaming-service' | 'standard';
  export type SectionItemAction = 'streaming-services' | 'my-recordings' | 'watch-tv' | 'tv-guide';
  export type SectionItemCondition = 'free' | '-1';

  export enum PageSectionStylePattern {
    WIDE = 'wide',
    POSTER = 'poster',
    CHANNEL_ONGOING = 'channel-ongoing',
    CHANNEL = 'channel',
    STREAMING_SERVICE = 'streaming-service',
    BANNER_FULLSCREEN = 'banner-fullscreen',
    PROMO_SINGLE = 'promo-single',
    PROMO_WIDE = 'promo-wide',
    PROMO_WIDE_LARGE = 'promo-wide-large',
    GENRE = 'genre',
    GROUP = 'group',
    APP = 'app',
    PERSON = 'person',
    KIDS_SERVICE = 'kids-service',
    POSTER_NOTEXT = 'poster-notext',
    WIDE_NOTEXT_LARGE = 'wide-notext-large',
    WIDE_NOTEXT_DOUBLE = 'wide-notext-double',
  }

  export enum SectionStyleType {
    SWIMLANE = 'swimlane',
    CAROUSEL = 'carousel',
    LIVE_PROGRAMS = 'live-programs',
    USER_CHANNELS_ONGOING = 'user-channels-ongoing',
    USER_CHANNELS_CATCHUP = 'user-channels-catchup',
    CONTINUE_WATCHING = 'continue-watching',
    BOUGHT_AND_RENTED = 'bought-and-rented',
    BOUGHT = 'bought',
    RENTED = 'rented',
    FAVOURITES = 'favourites', // TODO: Delete when property changed to American 'favorites'
    FAVORITES = 'favorites', // TODO: In case ATS change this property to American english
    MY_RECORDINGS = 'my-recordings',
    APPS = 'apps',
    SORTABLE_GRID = 'sortable-grid',
    PLAIN_GRID = 'plain-grid',
    // TODO: Does this still exist?
    CHANNEL_FILTERABLE_GRID = 'channel-filterable-grid',
    STREAMING_SERVICES_FOOTER = 'streaming-services-footer',
  }

  export enum PageViewStyleType {
    GRID = 'grid',
    SWIMLANE = 'swimlane',
  }

  export enum SectionItemsOrderType {
    NAME = 'name',
    PRODUCTION_YEAR = 'productionYear',
    TOTAL_EPISODE_COUNT = 'totalEpisodeCount',
  }

  export type SectionItemsOrderBy = 'name' | 'productionYear' | 'totalEpisodeCount';
}
